import React, { PureComponent } from 'react';
import StepCard from '../../../components/StepCard';
import { Banner } from 'Common/components/Molecules/Banner';
import { Card } from 'Common/components/Atoms/Card';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { CardHeader } from 'Common/components/Atoms/CardHeader';
import { Flex } from 'Common/components/Atoms/Flex';
import { Text } from 'Common/components/Atoms/Text';
import { TwoFactor } from 'Common/components/Molecules/2fa';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { actions as accountActions } from '../../../reducers/Account';
import {
  accountUserSelector,
  isUpdatingAccountSelector,
} from '../../../selectors/Account';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { User } from '../../../models/User';
import { logEvent, REVIEWER_ONBOARDING_TWO_FACTOR } from 'utils/analytics';

export interface Props {
  theme: Theme;
  user: User;
  generate2faCode: (callback: (url: string) => void) => void;
  enable2fa: (
    token: string,
    callback: (code: string, error: string) => void
  ) => void;
  handleSubmit: () => void;
  isUpdating: boolean;
}

interface State {
  codeUrl?: string;
  isSubmitting: boolean;
  recoveryCode?: string;
  continueError: boolean;
  tfaError?: string;
  twoFactorToken?: string;
}

export class TwoFactorAuth extends PureComponent<Props, State> {
  state: State = {
    codeUrl: undefined,
    isSubmitting: false,
    recoveryCode: undefined,
    continueError: false,
    tfaError: undefined,
    twoFactorToken: undefined,
  };

  componentDidMount() {
    const { generate2faCode, user } = this.props;
    if (!user.has_2fa) {
      generate2faCode((url) => {
        this.setState({ codeUrl: url });
      });
    }

    logEvent(REVIEWER_ONBOARDING_TWO_FACTOR);
  }

  onSubmitCode = (twoFactorToken: string) => {
    const { enable2fa } = this.props;
    this.setState({ isSubmitting: true });
    enable2fa(twoFactorToken, (recoveryCode, tfaError) => {
      this.setState({
        tfaError,
        isSubmitting: false,
        recoveryCode,
        twoFactorToken: recoveryCode ? '' : twoFactorToken,
      });
    });
  };

  handleSubmit = () => {
    const { handleSubmit, user } = this.props;
    if (!user.has_2fa) {
      this.setState({ continueError: true });
    } else {
      handleSubmit();
    }
  };

  render(): JSX.Element {
    const { theme, user, isUpdating } = this.props;
    const { codeUrl, recoveryCode, isSubmitting, continueError, tfaError } =
      this.state;
    return (
      <StepCard
        handleSubmit={this.handleSubmit}
        showPreviousButton={false}
        isUpdating={isUpdating}
      >
        <CardHeader>
          <Text variant="h3">Enable Two-Factor Authentication</Text>
        </CardHeader>
        <Text variant="body1" color="textSecondary">
          In an ongoing effort to ensure maximum security for our customers, all
          reviewers are required to enable two-factor authentication. In order
          to use two-factor authentication, you <b>must</b> install the Google
          Authenticator app on your smartphone. Google Authenticator is
          available for Android and iOS.
        </Text>
        {!user.has_2fa ? (
          <React.Fragment>
            <Flex justifyContent="center" center my={6}>
              {codeUrl ? (
                <img src={codeUrl} height="130" width="130" alt="QR Code" />
              ) : (
                <ContentLoader height="130px" width="130px" />
              )}
            </Flex>
            <Flex justifyContent="center" center height={50}>
              {!isSubmitting ? (
                <TwoFactor
                  handleSubmitCallback={this.onSubmitCode}
                  autoFocus={true}
                />
              ) : (
                <Text variant="body1" color="textSecondary">
                  Please wait...
                </Text>
              )}
            </Flex>
            {tfaError && (
              <Text variant="body1" color="error" textAlign="center" mt={3}>
                {tfaError}
              </Text>
            )}
            {continueError && (
              <Text variant="body1" color="error" textAlign="center" mt={3}>
                You must enable two-factor authentication in order to proceed.
              </Text>
            )}
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Banner color={theme.palette.success.main} icon={faLock} my={6}>
              <Text variant="body1" ml={2}>
                <b>Enabled.</b> Two-Factor authentication is enabled using
                Google Authenticator for the account <b>{user.email}</b>.
              </Text>
            </Banner>
            {recoveryCode && (
              <Card p={3} display="block">
                <Text variant="body1" mb={2}>
                  If you lose your two-factor authentication device, you may use
                  this emergency reset token to disable two-factor
                  authentication on your account. This is the only time this
                  token will be displayed, so be sure not to lose it!
                </Text>
                <Text variant="h4">
                  <code>{recoveryCode}</code>
                </Text>
              </Card>
            )}
          </React.Fragment>
        )}
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const user = accountUserSelector(state);
  const isUpdating = isUpdatingAccountSelector(state);

  return {
    user,
    isUpdating,
  };
}

function mapDispatchToProps(dispatch: Dispatch<{}>) {
  return bindActionCreators(accountActions, dispatch);
}

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(TwoFactorAuth)
);
